import React, { useMemo } from 'react'
import { motion } from 'framer-motion'

export interface OrderBookBarData {
  month: string // Short month name: 'Oct', 'Nov', etc.
  value: number // Value for the bar height (0-100 or actual value)
  isHighlighted?: boolean // If true, uses yellow-green color, otherwise gray
}

interface OrderBookGraphProps {
  data?: OrderBookBarData[]
  className?: string
}

// Default data for demonstration
const DEFAULT_DATA: OrderBookBarData[] = [
  { month: 'Oct', value: 60, isHighlighted: true },
  { month: 'Nov', value: 30, isHighlighted: false },
  { month: 'Dec', value: 90, isHighlighted: true },
  { month: 'Jan', value: 60, isHighlighted: true },
  { month: 'Feb', value: 40, isHighlighted: false },
  { month: 'Mar', value: 90, isHighlighted: true },
]

const OrderBookGraph: React.FC<OrderBookGraphProps> = ({ 
  data = DEFAULT_DATA,
  className = ''
}) => {
  // Calculate normalized heights based on max value
  // Max height for bars is 64px (graph area height, reduced by 20%)
  const normalizedData = useMemo(() => {
    const maxValue = Math.max(...data.map(d => d.value), 1)
    return data.map(item => ({
      ...item,
      height: (item.value / maxValue) * 64 // Max height is 64px (reduced from 80px)
    }))
  }, [data])

  // Reverse data to match right-to-left order (Mar, Feb, Jan, Dec, Nov, Oct)
  const reversedData = [...normalizedData].reverse()

  return (
    <div className={`relative w-full max-w-[214px] h-[80px] ${className}`}>
      {/* Bars - positioned absolutely from right to left */}
      {reversedData.map((item, index) => {
        const isHighlighted = item.isHighlighted ?? false
        
        // Left positions: Mar=180px, Feb=146px, Jan=112px, Dec=78px, Nov=44px, Oct=10px
        const leftPositions = [180, 146, 112, 78, 44, 10]
        const leftPosition = leftPositions[index]
        
        // Gradient colors based on highlight
        const gradientFrom = isHighlighted ? '#e3fd5a' : '#595959'
        const gradientVia = isHighlighted ? 'rgba(244,254,192,0.24)' : 'rgba(108,108,108,0.24)'
        
        // All columns have left border, first column (Mar, index 0) also has right border
        const borderStyle = '1px dashed rgba(45,41,35,0.32)'
        
        return (
          <div
            key={`${item.month}-${index}`}
            className="absolute top-0 w-[34px] h-[64px]"
            style={{
              left: `${leftPosition}px`,
              borderLeft: borderStyle,
              borderRight: index === 0 ? borderStyle : 'none', // Mar (first in reversed) has right border
            }}
          >
            {/* Gradient bar inside */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: item.height, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[18px]"
              style={{
                background: `linear-gradient(to bottom, ${gradientFrom} 19.712%, ${gradientVia} 77.404%, rgba(255,255,255,0) 100%)`,
              }}
            />
          </div>
        )
      })}
          
      {/* Month labels - positioned absolutely at bottom, below graph area */}
      {reversedData.map((item, index) => {
        // Right positions from design: Mar=17px, Feb=51px, Jan=85px, Dec=119px, Nov=153px, Oct=187px
        const rightPositions = [17, 51, 85, 119, 153, 187]
        const rightPosition = rightPositions[index]
        return (
          <p
            key={`label-${item.month}-${index}`}
            className="absolute top-[72px] w-[34px] font-plus-jakarta text-[9px] leading-3 text-center text-[rgba(45,41,35,0.6)] dark:text-[rgba(253,253,253,0.6)] font-normal"
            style={{
              right: `${rightPosition}px`,
              transform: 'translateX(50%)'
            }}
          >
            {item.month}
          </p>
        )
      })}
      
      {/* Average line */}
      <div className="absolute left-0 top-[36px] w-full h-px bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(253,253,253,0.72)]" />

      {/* Avg label */}
      <div className="absolute left-0 top-[28px] flex items-center justify-center py-1 px-1 bg-[#2d2923] dark:bg-[#fdfdfd]">
        <p className="text-center text-white dark:text-[#0a0a0a] text-[11px] leading-3 font-semibold">
          Avg
        </p>
      </div>
    </div>
  )
}

export default OrderBookGraph
